/**
 * Hourly-rate configuration for billing.
 *
 * Rates are intentionally stored client-side (localStorage), not in the synced
 * schema: billing rates are a private, per-user reporting concern that must work
 * fully offline and doesn't warrant a schema migration across the offline-first
 * sync pipeline. A single global default applies to every project unless a
 * per-project override exists.
 */

const GLOBAL_RATE_KEY = "billing_global_rate";
const PROJECT_RATES_KEY = "billing_project_rates";

const DEFAULT_GLOBAL_RATE = 0;

function safeParseNumber(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Reads the global default hourly rate (applies when a project has no override). */
export function getGlobalRate(): number {
  if (typeof localStorage === "undefined") {
    return DEFAULT_GLOBAL_RATE;
  }
  return safeParseNumber(localStorage.getItem(GLOBAL_RATE_KEY)) ?? DEFAULT_GLOBAL_RATE;
}

/** Persists the global default hourly rate. */
export function setGlobalRate(rate: number): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(GLOBAL_RATE_KEY, String(rate));
}

/** Reads the full map of per-project rate overrides ({ projectId: rate }). */
export function getProjectRates(): Record<string, number> {
  if (typeof localStorage === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(PROJECT_RATES_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, number> = {};
      const record: Record<string, unknown> = { ...parsed };
      for (const key of Object.keys(record)) {
        const n = Number(record[key]);
        if (Number.isFinite(n)) {
          out[key] = n;
        }
      }
      return out;
    }
  } catch {
    /* corrupt payload → fall through to empty map */
  }
  return {};
}

/** Sets (or clears, when `rate` is null) the per-project rate override. */
export function setProjectRate(projectId: string, rate: number | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const map = getProjectRates();
  if (rate === null) {
    delete map[projectId];
  } else {
    map[projectId] = rate;
  }
  localStorage.setItem(PROJECT_RATES_KEY, JSON.stringify(map));
}

/**
 * Resolves the effective hourly rate for a project: its override if present,
 * otherwise the global default.
 */
export function resolveRate(
  projectId: string | null | undefined,
  projectRates: Record<string, number>,
  globalRate: number,
): number {
  if (projectId && projectId in projectRates) {
    return projectRates[projectId] ?? globalRate;
  }
  return globalRate;
}
